import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { ShoppingBag, Plus, Loader2, Unplug, KeyRound, History } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Loading, ErrorState, EmptyState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import {
  useZaloAccounts, useDeleteZaloAccount, statusMeta, PLATFORM,
  useShopeeConfig, useSaveShopeeConfig, useConnectShopee, useImportShopeeToken, useBackfillShopee,
} from '@/hooks/use-integrations'
import { ChannelCard } from './channel-card'
import { ChannelSetupGuide } from './channel-setup-guide'

/**
 * Tab Shopee — kết nối chat sàn Shopee qua Seller Chat API (chính chủ).
 *
 * Điều kiện phía Shopee: app trên Open Platform phải được cấp quyền Chat API
 * (module 109) và Webchat Push (code 10). Chưa có quyền thì cấu hình xong vẫn
 * không nhận được tin — tab nói rõ điều đó thay vì để nhân viên chờ.
 */
export function ShopeeTab() {
  const { data, isLoading, isError } = useZaloAccounts()
  const { data: cfg } = useShopeeConfig()
  const saveCfg = useSaveShopeeConfig()
  const connect = useConnectShopee()
  const importToken = useImportShopeeToken()
  const backfill = useBackfillShopee()
  const remove = useDeleteZaloAccount()

  const [partnerId, setPartnerId] = useState('')
  const [partnerKey, setPartnerKey] = useState('')
  const [shopId, setShopId] = useState('')
  const [refreshToken, setRefreshToken] = useState('')

  const shops = useMemo(() => (data ?? []).filter((a) => a.platform === PLATFORM.SHOPEE_CHAT), [data])

  const handleSaveCfg = () => {
    if (!/^\d+$/.test(partnerId.trim())) { toast.error('Partner ID phải là số'); return }
    saveCfg.mutate({ partnerId: partnerId.trim(), partnerKey: partnerKey.trim() || undefined }, {
      onSuccess: () => { toast.success('Đã lưu cấu hình Shopee (khoá được mã hoá)'); setPartnerKey('') },
      onError: (e) => toast.error(apiError(e)),
    })
  }
  const handleConnect = () => connect.mutate(undefined, {
    onSuccess: (url) => { window.location.href = url },
    onError: (e) => toast.error(apiError(e)),
  })
  const handleImport = () => {
    if (!shopId.trim() || !refreshToken.trim()) { toast.error('Cần Shop ID và Refresh Token'); return }
    importToken.mutate({ shopId: shopId.trim(), refreshToken: refreshToken.trim() }, {
      onSuccess: (r) => { toast.success(r.note); setRefreshToken('') },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Nhận và trả lời chat Shopee ngay trong hệ thống — nhân viên và AI dùng chung một luồng như Zalo.
        </p>
        <Button onClick={handleConnect} disabled={connect.isPending || !cfg?.configured}>
          {connect.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
          Uỷ quyền shop Shopee
        </Button>
      </div>

      {cfg && (
        <ChannelSetupGuide
          title={cfg.configured ? `Ứng dụng Shopee — Partner ID ${cfg.partnerId}` : 'Chưa cấu hình ứng dụng Shopee'}
          intro={cfg.chatPermissionNote}
          steps={[
            <>Vào <a href="https://open.shopee.com" target="_blank" rel="noreferrer" className="font-medium text-primary underline">open.shopee.com</a> → Console → app của shop (loại <strong>Seller In-house</strong>). Lấy <strong>Partner ID</strong> và <strong>Partner Key</strong> (Live).</>,
            <>Xin cấp quyền <strong>Chat API (module 109)</strong> qua Account Manager của shop — không qua ticket. Chưa có quyền thì gọi API sẽ bị <code className="rounded bg-background px-1">error_permission</code>.</>,
            <>Trong <strong>Push Mechanism</strong>, đăng ký Callback URL bên dưới và bật <strong>Webchat Push (Code 10)</strong>.</>,
            <>Bấm <strong>Uỷ quyền shop Shopee</strong> và đăng nhập tài khoản chủ shop để xác nhận. Uỷ quyền có hiệu lực 1 năm.</>,
          ]}
          copyItems={[
            { label: 'Redirect URL (uỷ quyền)', value: cfg.redirectUri },
            { label: 'Callback URL (Push Mechanism)', value: cfg.webhookUrl },
          ]}
          missing={cfg.configured ? [] : ['Partner ID', 'Partner Key']}
          footnote={
            <>
              <strong>Một cặp token cho một shop.</strong> Refresh token của Shopee dùng một lần: nếu CRM (in vận đơn) cũng đang
              xoay token của cùng shop này, hai bên sẽ làm nhau mất phiên. Chỉ uỷ quyền/nhập token cấp riêng cho ChatMQL.
            </>
          }
        />
      )}

      <div className="grid gap-3 rounded-lg border p-4 md:grid-cols-[1fr_1fr_auto]">
        <Input placeholder="Partner ID (số)" value={partnerId} onChange={(e) => setPartnerId(e.target.value)} />
        <Input placeholder={cfg?.configured ? 'Partner Key — để trống nếu giữ nguyên' : 'Partner Key (Live)'} type="password" value={partnerKey} onChange={(e) => setPartnerKey(e.target.value)} />
        <Button variant="secondary" onClick={handleSaveCfg} disabled={saveCfg.isPending}>
          {saveCfg.isPending ? <Loader2 className="animate-spin" /> : <KeyRound />} Lưu cấu hình
        </Button>
      </div>

      <details className="rounded-lg border p-4">
        <summary className="cursor-pointer text-sm font-medium">Nhập token thủ công (khi đã có cặp token cấp riêng cho ChatMQL)</summary>
        <div className="mt-3 grid gap-3 md:grid-cols-[180px_1fr_auto]">
          <Input placeholder="Shop ID" value={shopId} onChange={(e) => setShopId(e.target.value)} />
          <Input placeholder="Refresh Token" type="password" value={refreshToken} onChange={(e) => setRefreshToken(e.target.value)} />
          <Button variant="secondary" onClick={handleImport} disabled={importToken.isPending}>
            {importToken.isPending ? <Loader2 className="animate-spin" /> : <Plus />} Nhập token
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Hệ thống xoay token ngay để xác nhận còn sống — token cũ hết hiệu lực sau bước này.</p>
      </details>

      {isLoading ? (
        <Loading label="Đang tải shop Shopee..." />
      ) : isError ? (
        <ErrorState />
      ) : !shops.length ? (
        <EmptyState icon={ShoppingBag} title="Chưa kết nối shop Shopee nào" description="Cấu hình Partner ID/Key rồi bấm Uỷ quyền shop Shopee." />
      ) : (
        <div className="space-y-3">
          {shops.map((acc) => (
            <ChannelCard
              key={acc.id}
              icon={<ShoppingBag className="h-5 w-5" />}
              avatarUrl={acc.avatarUrl}
              title={acc.displayName || 'Shopee Shop'}
              subtitle={<>{acc.externalPageId ? `Shop ID ${acc.externalPageId} · ` : ''}{acc.lastConnectedAt ? `Kết nối lần cuối ${dayjs(acc.lastConnectedAt).format('DD/MM/YYYY HH:mm')}` : 'Chưa từng kết nối'}</>}
              status={statusMeta(acc.liveStatus ?? acc.status, acc.isDisabled)}
              actions={
                <>
                  <Button variant="ghost" size="sm" title="Kéo hội thoại & tin cũ" disabled={backfill.isPending}
                    onClick={() => backfill.mutate({ accountId: acc.id }, { onSuccess: (r) => toast.success(r.message), onError: (e) => toast.error(apiError(e)) })}>
                    <History />
                  </Button>
                  <Button variant="ghost" size="sm" title="Ngắt kết nối" disabled={remove.isPending}
                    onClick={() => remove.mutate(acc.id, { onSuccess: () => toast.success('Đã ngắt kết nối shop Shopee'), onError: (e) => toast.error(apiError(e)) })}>
                    <Unplug />
                  </Button>
                </>
              }
            />
          ))}
        </div>
      )}
    </div>
  )
}
